import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LivePolls from '../components/LivePolls';
import { Share2, ArrowLeft, Vote, AlertCircle, Shield, User, Radio, Lock, ArrowRight, Loader2 } from 'lucide-react';
import { cn, formatTime, getDurationInSeconds } from '../utils';
import { getTheme } from '../utils/themes';
import { useRoom } from '../hooks/useRoom';
import { SESSION_ROLES } from '../utils/permissions';

const PollRoom = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    roomData,
    sessionState,
    sessionRole,
    setSessionRole,
    timeLeft,
    isUnlocked,
    showPasswordPrompt,
    accessError,
    isInitialLoading,
    isModerator,
    handlePasswordSubmit,
    handleInvite
  } = useRoom(id);

  const [passwordInput, setPasswordInput] = useState('');
  const theme = getTheme('poll');

  const onPasswordSubmit = (e) => {
    e.preventDefault();
    handlePasswordSubmit(passwordInput);
  };

  if (isInitialLoading || !roomData) {
    return (
      <div className="min-h-screen bg-brand-light flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className={cn("animate-spin mx-auto mb-4", theme.text)} />
          <p className="text-gray-500 font-medium mb-4">Loading poll room...</p>
          <button onClick={() => navigate(-1)} className={cn("font-bold text-sm", theme.text)}>Go Back</button>
        </div>
      </div>
    );
  }

  const expectedDurationSeconds = getDurationInSeconds(roomData?.settings?.duration);

  return (
    <div className="min-h-screen bg-brand-light">
      <AnimatePresence>
        {showPasswordPrompt && !isUnlocked && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-brand-light flex items-center justify-center px-4"
          >
            <div className={cn("max-w-md w-full bg-white rounded-[2.5rem] border border-gray-100 p-8 shadow-2xl text-center", theme.shadow)}>
              <div className={cn("w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6", theme.lightBg, theme.text)}>
                <Lock size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Private Poll</h2>
              <p className="text-gray-500 font-medium mb-8">This poll is password protected. Please enter the access code to join.</p>
              
              <form onSubmit={onPasswordSubmit} className="space-y-4">
                <div>
                  <input 
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="Enter password"
                    className={cn(
                      "w-full px-6 py-4 bg-gray-50 border-2 rounded-2xl font-bold transition-all text-center text-xl tracking-widest outline-none",
                      theme.ring,
                      accessError ? "border-red-100 bg-red-50 text-red-600 focus:ring-red-100" : cn("border-gray-50", `focus-within:${theme.border}`)
                    )}
                    autoFocus
                  />
                  {accessError && (
                    <p className="text-red-500 text-xs font-bold mt-3 animate-in fade-in slide-in-from-top-1">{accessError}</p>
                  )}
                </div>
                <button 
                  type="submit"
                  className={cn("w-full text-white font-bold py-4 rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2", theme.bg, theme.shadow, theme.hover)}
                >
                  Access Poll
                  <ArrowRight size={20} />
                </button>
                <button 
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full text-gray-400 font-bold py-2 text-sm hover:text-gray-600 transition-colors"
                >
                  Go Back
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(-1)} className={cn("p-2 hover:bg-gray-100 rounded-xl transition-colors shrink-0", theme.text)}>
              <ArrowLeft size={20} />
            </button>
            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg shrink-0", theme.bg, theme.shadow)}>
               <Radio size={20} className={cn(sessionState === 'live' && "animate-pulse")} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <span className={cn("hidden xs:inline-block text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-widest border shrink-0", theme.lightBg, theme.text, theme.lightBorder)}>Live Poll</span>
                <h1 className="text-sm md:text-lg font-bold text-gray-900 truncate max-w-[150px] sm:max-w-none">{roomData.title}</h1>
              </div>
              <p className="text-[10px] md:text-xs text-gray-500 font-medium truncate opacity-80">
                {roomData.description || `Hosted by ${roomData.host || 'Host'} • ${roomData.participants || 0} participants`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-4 shrink-0">
            <div className={cn(
              "flex items-center gap-1.5 px-3 py-2 rounded-xl border font-bold text-[10px] md:text-xs shadow-sm",
              sessionState === 'upcoming' ? "bg-amber-50 text-amber-600 border-amber-100" : 
              (expectedDurationSeconds > 0 && timeLeft > expectedDurationSeconds) ? "bg-orange-50 text-orange-600 border-orange-100" : cn(theme.lightBg, theme.text, theme.lightBorder)
            )}>
              <div className={cn(
                "w-2 h-2 rounded-full animate-pulse shadow-[0_0_8px_currentColor]",
                sessionState === 'upcoming' ? "bg-amber-400" : theme.bg
              )} />
              <span className="tabular-nums">{formatTime(timeLeft)}</span>
            </div>
            <button 
              onClick={handleInvite}
              className={cn("p-2 sm:p-2.5 rounded-xl transition-all shadow-sm", theme.lightBg, theme.text, `hover:${theme.bg}`, "hover:text-white", "hover:shadow-md active:scale-95")}
              title="Share Session"
            >
              <Share2 size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-2 sm:px-4 py-8 md:py-12">
        <div className={cn("bg-white rounded-[3rem] border shadow-2xl overflow-hidden flex flex-col", theme.lightBorder, theme.shadow)}>
           <div className={cn("p-8 border-b text-center relative", theme.lightBorder, theme.lightBg)}>
              {roomData.image && (
                <div className="absolute inset-0 z-0">
                  <img src={roomData.image} alt="" className="w-full h-full object-cover opacity-10" />
                  <div className={cn("absolute inset-0 bg-gradient-to-b from-transparent", theme.lightBg)}></div>
                </div>
              )}
              {isModerator && (
                <div className="absolute top-4 right-4">
                   <span className={cn("text-xxs font-bold px-2 py-0.5 rounded-full uppercase tracking-widest border", theme.text, theme.lightBg, theme.lightBorder)}>Moderator View</span>
                </div>
              )}
              <div className="relative z-10">
                <div className={cn("w-16 h-16 bg-white rounded-2xl shadow-xl flex items-center justify-center mx-auto mb-6 border", theme.text, theme.shadow, theme.lightBorder)}>
                   <Vote size={32} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{roomData.title}</h2>
                <div className="flex items-center justify-center gap-4 text-xs font-bold text-gray-400">
                   <span>{roomData.active_participants_count ?? roomData.participants ?? 0} participants</span>
                   <span className="w-1 h-1 bg-gray-200 rounded-full"></span>
                   <span className={theme.text}>Verified Poll</span>
                </div>
              </div>
           </div>
           
           <div className="p-8">
              <LivePolls 
                sessionState={sessionState} 
                isModerator={isModerator} 
                sessionId={roomData?.id}
                communityId={roomData?.community_id}
                roomData={roomData}
              />
           </div>

           <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex items-center gap-3">
              <AlertCircle size={16} className={theme.text} />
              <p className="text-xxs text-gray-400 font-bold uppercase tracking-wider">
                 Your vote is anonymous and cannot be changed after submission.
              </p>
           </div>
        </div>
      </div>

      {isModerator && (
        <div className={cn("fixed bottom-32 md:bottom-8 left-1/2 -translate-x-1/2 bg-white/80 backdrop-blur-xl border p-2 rounded-2xl shadow-2xl flex gap-2 z-40", theme.lightBorder)}>
            <button 
              onClick={() => setSessionRole(SESSION_ROLES.HOST)}
              className={cn("p-3 rounded-xl transition-all", sessionRole === SESSION_ROLES.HOST ? cn(theme.bg, "text-white shadow-lg", theme.shadow) : "text-gray-400 hover:bg-gray-100")}
              title="Host View"
            >
              <Shield size={20} />
            </button>
            <button 
              onClick={() => setSessionRole(SESSION_ROLES.PARTICIPANT)}
              className={cn("p-3 rounded-xl transition-all", sessionRole === SESSION_ROLES.PARTICIPANT ? cn(theme.bg, "text-white shadow-lg", theme.shadow) : "text-gray-400 hover:bg-gray-100")}
              title="Participant View"
            >
              <User size={20} />
            </button>
        </div>
      )}
    </div>
  );
};

export default PollRoom;
